import React, { useEffect, useState } from 'react';
import { AlertCircle, BellRing, CheckCircle, Mail, RefreshCw, Save, Siren } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from '../hooks/use-toast';
import { aiSettingsAPI } from '../api';

const AIAvisosFallos = () => {
  const [email, setEmail] = useState('');
  const [umbral, setUmbral] = useState(3);
  const [activa, setActiva] = useState(true);
  const [horas, setHoras] = useState(6);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [noDisponible, setNoDisponible] = useState(false);
  const [simulando, setSimulando] = useState(false);
  const [enviarCorreo, setEnviarCorreo] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      const { data } = await aiSettingsAPI.getAlerta();
      setEmail(data.email || '');
      setUmbral(data.umbral || 3);
      setActiva(data.activa !== false);
      setHoras(data.horas_entre_avisos || 6);
    } catch (e) {
      if (e.response?.status === 404) setNoDisponible(true);
    } finally {
      setCargando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await aiSettingsAPI.updateAlerta({ email, umbral: Number(umbral), activa });
      toast({ title: 'Guardado', description: 'Aviso por fallos de IA actualizado.' });
      await cargar();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.detail || 'No se pudo guardar',
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const simular = async () => {
    setSimulando(true);
    setResultado(null);
    try {
      const { data } = await aiSettingsAPI.simularFallo({ enviar_email: enviarCorreo });
      setResultado(data);
      toast({
        title: data.todo_ok ? 'Respaldo comprobado' : 'Hay algo que revisar',
        description: data.todo_ok
          ? 'El respaldo responde y el aviso está configurado.'
          : 'Mira el detalle de los pasos.',
        variant: data.todo_ok ? undefined : 'destructive',
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.detail || 'No se pudo simular',
        variant: 'destructive',
      });
    } finally {
      setSimulando(false);
    }
  };

  if (cargando) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-rose-600" />
      </div>
    );
  }

  if (noDisponible) {
    return (
      <Card data-testid="ai-avisos-no-disponible" className="border-2 border-gray-200">
        <CardContent className="pt-6 text-sm text-gray-600 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
          El aviso por fallos de IA necesita una actualización del servidor que todavía no está
          publicada. En cuanto se aplique, este recuadro se activará solo.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ai-avisos-card" className="border-2 border-rose-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellRing className="w-5 h-5 text-rose-600" />
          Aviso cuando la IA deja de responder
        </CardTitle>
        <CardDescription>
          Si un proveedor encadena fallos, te llega un correo con el motivo y qué hacer. Como
          mucho un aviso cada {horas} horas por proveedor, para no llenarte el buzón.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200">
          <div>
            <Label className="font-semibold">Avisos activados</Label>
            <p className="text-sm text-gray-600">Desactívalos si no quieres recibir ningún correo.</p>
          </div>
          <Switch data-testid="ai-aviso-activa" checked={activa} onCheckedChange={setActiva} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              Dirección de aviso
            </Label>
            <Input
              data-testid="ai-aviso-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="responsable@lexduo.cat"
            />
          </div>
          <div className="space-y-2">
            <Label>Fallos seguidos antes de avisar</Label>
            <Input
              data-testid="ai-aviso-umbral"
              type="number"
              min={1}
              max={50}
              value={umbral}
              onChange={(e) => setUmbral(e.target.value)}
            />
          </div>
        </div>

        <Button
          data-testid="ai-aviso-guardar"
          onClick={guardar}
          disabled={guardando}
          className="w-full bg-rose-600 hover:bg-rose-700"
        >
          {guardando
            ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Guardando…</>
            : <><Save className="w-4 h-4 mr-2" />Guardar aviso</>}
        </Button>

        <div className="p-4 rounded-xl border-2 border-dashed border-rose-200 space-y-3">
          <div className="flex items-center gap-2 font-semibold">
            <Siren className="w-4 h-4 text-rose-600" />
            Prueba de caída
          </div>
          <p className="text-sm text-gray-600">
            Comprueba de verdad que hay un segundo proveedor, que responde, y que el aviso
            llegaría a su destino. No cambia nada de tu configuración.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              data-testid="ai-simular-enviar-correo"
              type="checkbox"
              checked={enviarCorreo}
              onChange={(e) => setEnviarCorreo(e.target.checked)}
              className="rounded border-gray-300"
            />
            Enviarme también el correo de prueba
          </label>
          <Button
            data-testid="ai-simular-btn"
            variant="outline"
            onClick={simular}
            disabled={simulando}
            className="w-full"
          >
            {simulando
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Comprobando…</>
              : <>Simular caída del proveedor principal</>}
          </Button>

          {resultado && (
            <div data-testid="ai-simular-resultado" className="space-y-2 pt-1">
              {(resultado.pasos || []).map((p, i) => (
                <div
                  key={i}
                  className={`text-sm p-2.5 rounded-lg flex items-start gap-2 ${
                    p.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {p.ok
                    ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                  <div>
                    <div className="font-medium">{p.paso}</div>
                    <div className="text-xs opacity-80 break-words">{p.detalle}</div>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                <Badge variant="outline">Modo: {resultado.modo}</Badge>
                {resultado.principal && <Badge variant="outline">Principal: {resultado.principal}</Badge>}
                <Badge variant="outline">
                  Respaldo: {resultado.respaldo || 'ninguno'}
                </Badge>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AIAvisosFallos;
